import React from 'react';
import { Star, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();

  const handleAddToCart = async () => {
    await addToCart(product);
    toast.success(`${product.name} added to cart!`, {
      description: 'Continue shopping or proceed to checkout.',
    });
  };

  const discount = Math.round(
    ((product.original_price - product.price) / product.original_price) * 100
  );

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-[#D4A853]/10">
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-[#FDF6E3]">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        {/* Discount Badge */}
        {discount > 0 && (
          <div className="absolute top-4 left-4 bg-[#C17817] text-[#FDF6E3] px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            -{discount}%
          </div>
        )}
        {/* Quick Add Button */}
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
          <Button
            onClick={handleAddToCart}
            disabled={loading}
            className="w-full bg-[#5C3D2E] hover:bg-[#3D2314] text-[#FDF6E3] py-5 rounded-xl font-medium flex items-center justify-center gap-2 shadow-xl"
          >
            <ShoppingBag className="h-4 w-4" />
            Add to Cart
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Rating */}
        <div className="flex items-center gap-1 mb-2">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-3.5 w-3.5 ${
                  i < Math.floor(product.rating)
                    ? 'text-[#D4A853] fill-[#D4A853]'
                    : 'text-[#D4A853]/30'
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-[#5C3D2E]/60 ml-1">
            ({product.reviews})
          </span>
        </div>

        {/* Name */}
        <h3 className="font-serif text-lg font-semibold text-[#5C3D2E] leading-tight mb-1 group-hover:text-[#C17817] transition-colors duration-300">
          {product.name}
        </h3>

        {/* Weight */}
        <p className="text-sm text-[#8B6914] mb-3">{product.weight}</p>

        {/* Features */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {product.features.slice(0, 2).map((feature) => (
            <span
              key={feature}
              className="text-[10px] px-2 py-1 bg-[#FDF6E3] text-[#5C3D2E]/70 rounded-full"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Price */}
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-[#C17817]">
            ${product.price.toFixed(2)}
          </span>
          {product.original_price > product.price && (
            <span className="text-sm text-[#5C3D2E]/40 line-through">
              ${product.original_price.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
